import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  Edit, 
  Save, 
  X, 
  Lock, 
  Loader2,
  Calendar
} from "lucide-react";
import { SuperAdminLayout } from "@/components/admin/SuperAdminLayout";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { UserRole } from "@/lib/schema";
import { getReferralSettingsLegacy, updateReferralSettings, ReferralSettingsData } from "@/api/referralSettings";
import { formatRewardAmount } from "@/lib/textUtils";
import { CoinsIcon } from "@/components/ui/CoinsIcon";
import { LoadingScreen } from "@/components/ui/LoadingScreen";

interface SettingSection {
  id: 'cvUploadBonusAmount' | 'clientReferralBonusAmount' | 'noticePeriodDays';
  title: string;
  description: string;
  icon: typeof CoinsIcon | typeof Calendar;
  color: string;
  unit: string;
  placeholder: string;
}

export default function ReferralSettings() {
  const { userData, loading: authLoading } = useAuth();
  const { toast } = useToast();

  // State
  const [settings, setSettings] = useState<ReferralSettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<number>(0);

  // Check if user is super admin
  const isSuperAdmin = userData?.role === UserRole.SUPER_ADMIN;

  const sections: SettingSection[] = [
    {
      id: 'cvUploadBonusAmount',
      title: 'CV Upload Bonus',
      description: 'Bonus amount for uploading candidate CV',
      icon: CoinsIcon,
      color: 'orange',
      unit: 'R',
      placeholder: 'Enter bonus amount'
    },
    {
      id: 'clientReferralBonusAmount',
      title: 'Client Referral Bonus',
      description: 'Bonus amount for successful client referrals',
      icon: CoinsIcon,
      color: 'orange',
      unit: 'R',
      placeholder: 'Enter bonus amount'
    },
    {
      id: 'noticePeriodDays',
      title: 'Notice Period',
      description: 'Standard notice period in days',
      icon: Calendar,
      color: 'orange',
      unit: 'days',
      placeholder: 'Enter number of days'
    }
  ];

  // Load settings on component mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await getReferralSettingsLegacy();
      
      if (data) {
        setSettings(data);
      } else {
        // Initialize with default values
        const defaultSettings: ReferralSettingsData = {
          cvUploadBonusAmount: 500,
          clientReferralBonusAmount: 20000,
          noticePeriodDays: 90,
          lastUpdated: new Date(),
          updatedBy: ''
        };
        setSettings(defaultSettings);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      // TODO: Integrate with Sentry for error tracking and reporting
      toast({
        title: "Error",
        description: "Failed to load referral settings",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (sectionId: string) => {
    if (!settings) return;
    
    setEditingSection(sectionId);
    setEditValue(settings[sectionId as keyof ReferralSettingsData] as number);
  };

  const cancelEditing = () => {
    setEditingSection(null);
    setEditValue(0);
  };

  const saveSettings = async () => {
    if (!settings || !userData || !editingSection) return;
    
    try {
      setSaving(true);
      
      const updatedSettings = {
        cvUploadBonusAmount: settings.cvUploadBonusAmount,
        clientReferralBonusAmount: settings.clientReferralBonusAmount,
        noticePeriodDays: settings.noticePeriodDays,
        [editingSection]: editValue
      };
      
      const result = await updateReferralSettings(
        updatedSettings, 
        userData.email || `${userData.firstName} ${userData.lastName}` || 'Unknown'
      );
      
      if (result) {
        setSettings(result);
      }
      
      cancelEditing();
      
      toast({
        title: "Settings Saved",
        description: "Referral settings have been updated successfully"
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      // TODO: Integrate with Sentry for error tracking and reporting
      toast({
        title: "Error",
        description: "Failed to save settings",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  // Show loading or access denied
  if (authLoading || loading) {
    return (
      <SuperAdminLayout title="Referral Settings" subtitle="Configure referral program and commission settings">
        <LoadingScreen fullScreen={false} text="Loading settings..." />
      </SuperAdminLayout>
    );
  }

  if (!isSuperAdmin) {
    return (
      <SuperAdminLayout title="Referral Settings" subtitle="Configure referral program and commission settings">
        <Alert className="max-w-md mx-auto mt-20">
          <Lock className="h-4 w-4" />
          <AlertDescription>
            Access denied. Only super administrators can view this page.
          </AlertDescription>
        </Alert>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout 
      title="Referral Settings" 
      subtitle="Configure referral program and commission settings"
    >
        {/* Last Updated Info */}
        {settings?.lastUpdated && (
          <div className="text-sm text-gray-500 space-grotesk">
            Last updated: {settings.lastUpdated.toLocaleDateString()} by {settings.updatedBy}
          </div>
        )}

        {/* Settings Sections */}
        <div className="space-y-6">
          {sections.map((section) => {
            const isEditing = editingSection === section.id;
            const currentValue = settings?.[section.id] || 0;

            return (
              <Card key={section.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        {section.icon === CoinsIcon ? (
                          <CoinsIcon className={`h-5 w-5 text-${section.color}-600`} />
                        ) : (
                          <section.icon className={`h-5 w-5 text-${section.color}-600`} />
                        )}
                        {section.title}
                      </CardTitle>
                      <CardDescription>
                        {section.description}
                      </CardDescription>
                    </div>
                    {!isEditing && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => startEditing(section.id)}
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {isEditing ? (
                    <div className="space-y-4">
                      {/* Edit input */}
                      <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                          <Input
                            type="number"
                            value={editValue}
                            onChange={(e) => setEditValue(Number(e.target.value))}
                            placeholder={section.placeholder}
                            min="0"
                            step={section.id === 'noticePeriodDays' ? '1' : '1'}
                          />
                          <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-500">
                            {section.unit}
                          </span>
                        </div>
                      </div>

                      {/* Action buttons */}
                      <div className="flex items-center gap-2 pt-2">
                        <Button
                          variant="default"
                          onClick={saveSettings}
                          disabled={saving}
                          size="sm"
                          className="space-grotesk"
                        >
                          {saving ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Save className="h-4 w-4 mr-2" />
                          )}
                          Save Changes
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={cancelEditing}
                          disabled={saving}
                          size="sm"
                        >
                          <X className="h-4 w-4 mr-2" />
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-lg font-semibold text-gray-900 space-grotesk">
                      {section.unit === 'R' ? 'R' : ''}{section.unit === 'R' ? formatRewardAmount(currentValue) : currentValue}{section.unit === 'days' ? ' days' : ''}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
    </SuperAdminLayout>
  );
}
